import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import style from './kng-legal-acte-form.style.js';

/**
 * @class KngLegalActeForm
 * @extends LitElement
 *
 * @property {Array} owners - Available owners for the dropdown.
 * @property {Array} buildings - Available buildings for the dropdown.
 * @property {Array} tenants - Available tenants for the dropdown.
 * @property {Array} cases - Available case identifiers for the dropdown.
 * @property {string} ownerCode - Selected owner code.
 * @property {string} buildingRef - Selected building reference.
 * @property {string} tenantRef - Selected tenant reference.
 * @property {string} caseId - Selected case identifier.
 * @property {string} actType - Selected act type.
 * @property {string} actDate - Selected act date.
 * @property {number} durationHours - Selected duration in hours.
 * @property {string} notes - Notes content.
 *
 * @fires kng-acte-submit - Fired when the form is submitted.
 * @fires kng-owner-change - Fired when the owner selection changes.
 * @fires kng-building-change - Fired when the building selection changes.
 *
 * @example
 * <kng-legal-acte-form></kng-legal-acte-form>
 */
export class KngLegalActeForm extends LitElement {
  static properties = {
    ownerCode: { type: String },
    buildingRef: { type: String },
    tenantRef: { type: String },
    caseId: { type: String },
    actType: { type: String },
    actDate: { type: String },
    quantity: { type: Number },
    notes: { type: String },
    rates: { type: Array },
    actTypeOptions: { type: Array },
    rateId: { type: String },
    isFetchingRates: { type: Boolean },
    includeInReport: { type: Boolean },
    actSearchQuery: { type: String }
  };

  static styles = style;

  constructor() {
    super();
    this.ownerCode = '';
    this.buildingRef = '';
    this.tenantRef = '';
    this.caseId = '';
    this.actType = '';
    this.actDate = new Date().toISOString().slice(0, 16);
    this.quantity = 1;
    this.notes = '';
    this.rates = [];
    this.actTypeOptions = [];
    this.rateId = '';
    this.isFetchingRates = false;
    this.includeInReport = true;
    this.actSearchQuery = '';
  }

  connectedCallback() {
    super.connectedCallback();
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('ownerCode') || changedProperties.has('actDate')) {
      this.#fetchAllRates();
    }
  }

  get currentRateAmount() {
    const selectedOpt = this.actTypeOptions.find(o => o.rateId === this.rateId);
    return selectedOpt ? selectedOpt.amount.toFixed(2) : '0.00';
  }

  get currentRateUnit() {
    const selectedOpt = this.actTypeOptions.find(o => o.rateId === this.rateId);
    if (!selectedOpt) return 'forfait';
    if (selectedOpt.rateType && selectedOpt.rateType !== 'exception') return selectedOpt.rateType.toLowerCase();
    if (selectedOpt.unit) return selectedOpt.unit.toLowerCase();
    return 'forfait';
  }

  get filteredActTypeOptions() {
    if (!this.actSearchQuery) return this.actTypeOptions;
    const query = this.actSearchQuery.toLowerCase();
    return this.actTypeOptions.filter(opt => 
      opt.actType.toLowerCase().includes(query) || 
      (opt.ownerCode && opt.ownerCode.toLowerCase().includes(query))
    );
  }

  render() {
    return html`
      <div class="panel-card" style="padding: var(--sl-spacing-medium); background: var(--sl-color-neutral-0); border: 1px solid var(--sl-color-neutral-200); border-radius: var(--sl-border-radius-large); margin: 0;">
        <div style="margin-bottom: var(--sl-spacing-small); border-bottom: 1px solid var(--sl-color-neutral-100); padding-bottom: var(--sl-spacing-x-small);">
          <strong style="text-transform: uppercase; color: var(--sl-color-brand-950); font-size: var(--sl-font-size-small);">Ajouter un acte</strong>
        </div>
        <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-medium);">
          
          <!-- Row 1: Type, Date, Rate display -->
          <div style="display: flex; gap: var(--sl-spacing-medium); align-items: center; flex-wrap: wrap;">
            <div style="flex: 2; min-width: 200px;">
              <sl-dropdown placement="bottom-start" class="act-type-dropdown" hoist>
                <sl-button slot="trigger" size="small" style="width: 100%; text-align: left; justify-content: space-between;">
                  ${this.actType ? html`${this.actType} ▾` : 'Élément facturé ▾ 🔍'}
                </sl-button>
                <div class="combobox-panel">
                  <sl-input size="small" placeholder="Rechercher..." clearable @sl-input=${this.#handleSearchChange} value=${this.actSearchQuery}></sl-input>
                  <sl-menu class="combobox-menu">
                    ${this.actTypeOptions.length === 0 ? html`<sl-menu-item disabled>Chargement...</sl-menu-item>` : ''}
                    ${this.filteredActTypeOptions.map(opt => html`
                      <sl-menu-item value="${opt.rateId}" @click=${() => this.#selectRate(opt.rateId)} style="font-family: monospace;">
                        <div style="display: grid; grid-template-columns: 1fr auto 90px; gap: 8px; width: 100%; align-items: center;">
                          <span style="white-space: normal; line-height: 1.2;">${opt.actType}</span>
                          <span>${opt.ownerCode ? html`<sl-badge variant="primary" pill>${opt.ownerCode}</sl-badge>` : ''}</span>
                          <span style="text-align: right;">CHF ${opt.amount.toFixed(2)}</span>
                        </div>
                      </sl-menu-item>
                    `)}
                    ${this.filteredActTypeOptions.length === 0 && this.actTypeOptions.length > 0 ? html`<sl-menu-item disabled>Aucun résultat</sl-menu-item>` : ''}
                  </sl-menu>
                </div>
              </sl-dropdown>
            </div>
            <div style="flex: 1; min-width: 150px;">
              <sl-input size="small" type="datetime-local" value=${this.actDate} @sl-input=${this.#handleActDateChange}></sl-input>
            </div>
            <div style="font-size: 0.9rem; color: var(--sl-color-neutral-600); white-space: nowrap;">
              Tarif CHF ${this.currentRateAmount} (${this.currentRateUnit})
            </div>
          </div>

          <!-- Row 2: Qty, Net amount -->
          <div style="display: flex; gap: var(--sl-spacing-medium); align-items: center; flex-wrap: wrap; justify-content: space-between;">
            <div style="display: flex; align-items: center; gap: var(--sl-spacing-small);">
              <span style="font-size: 0.9rem; color: var(--sl-color-neutral-600); white-space: nowrap;">Qté</span>
              <sl-input size="small" type="number" value=${String(this.quantity)} step="1" @sl-input=${this.#handleQuantityChange} style="width: 70px;"></sl-input>
              <span style="font-size: 0.9rem; color: var(--sl-color-neutral-500); white-space: nowrap;">× ${this.currentRateUnit}</span>
            </div>
            
            <div style="font-weight: bold; font-size: 0.95rem; color: var(--sl-color-neutral-900);">
              HT CHF ${this.#calculateMontantHT()}
            </div>
          </div>

          <!-- Row 3: Notes -->
          <div>
            <sl-input size="small" placeholder="Note" value=${this.notes} @sl-input=${this.#handleNotesChange}></sl-input>
          </div>
          
          <!-- Row 4: Actions -->
          <div style="display: flex; align-items: center; justify-content: space-between; margin-top: var(--sl-spacing-small);">
            <sl-checkbox size="small" ?checked=${this.includeInReport} @sl-change=${this.#handleIncludeChange}>Inclure au rapport</sl-checkbox>
            <sl-button size="small" variant="primary" @click=${this.#handleSubmit}>Proposer</sl-button>
          </div>

        </div>
      </div>
    `;
  }


  #handleActTypeChange(event) {
    this.actType = event.target.value || '';
  }

  #handleActDateChange(event) {
    this.actDate = event.target.value || '';
  }

  #handleSearchChange(event) {
    this.actSearchQuery = event.target.value || '';
  }

  #selectRate(rateId) {
    this.rateId = rateId;
    const selectedOpt = this.actTypeOptions.find(o => o.rateId === this.rateId);
    if (selectedOpt) {
      this.actType = selectedOpt.actType;
    }
  }

  async #fetchAllRates() {
    if (!this.ownerCode || !this.actDate) {
      this.actTypeOptions = [];
      return;
    }
    this.isFetchingRates = true;
    try {
      const response = await fetch(`/api/legal/rates?ownerCode=${encodeURIComponent(this.ownerCode)}&actDate=${encodeURIComponent(this.actDate)}`);
      const allRates = response.ok ? await response.json() : [];
      
      // Sort to prefer owner specific rate, then alphabetically by actType
      allRates.sort((a, b) => {
        if (a.ownerCode && !b.ownerCode) return -1;
        if (!a.ownerCode && b.ownerCode) return 1;
        return a.actType.localeCompare(b.actType);
      });

      this.actTypeOptions = allRates.map(r => ({
        actType: r.actType,
        rateId: r.id,
        amount: r.amount,
        ownerCode: r.ownerCode,
        rateType: r.rateType || null,   // PR2 §3.A: used for unit label (heure|forfait)
        unit: r.unit || r.unite,
        active: r.active !== false
      }));
      
      // Keep rateId mapped if actType already set (e.g. initial load), otherwise wait for user input
      if (this.actType && this.actTypeOptions.length > 0) {
         const selectedOpt = this.actTypeOptions.find(o => o.actType === this.actType);
         if (selectedOpt) {
           this.rateId = selectedOpt.rateId;
         } else {
           this.actType = '';
           this.rateId = '';
         }
      }
    } catch (e) {
      console.error('Error fetching all rates', e);
    } finally {
      this.isFetchingRates = false;
    }
  }

  #calculateMontantHT() {
    const selectedOpt = this.actTypeOptions.find(o => o.rateId === this.rateId);
    if (!selectedOpt) return '0.00';
    return (selectedOpt.amount * this.quantity).toFixed(2);
  }

  #handleQuantityChange(event) {
    this.quantity = parseFloat(event.target.value) || 1;
  }

  #handleNotesChange(event) {
    this.notes = event.target.value || '';
  }

  #handleIncludeChange(event) {
    this.includeInReport = event.target.checked;
  }

  #handleCancel() {
    this.notes = '';
    this.quantity = 1;
    // Dispatch a cancel event in case parent wants to hide it
    this.dispatchEvent(new CustomEvent('kng-acte-cancel', { bubbles: true, composed: true }));
  }

  #handleSubmit() {
    this.dispatchEvent(new CustomEvent('kng-acte-submit', {
      detail: {
        caseId: this.caseId,
        ownerCode: this.ownerCode,
        buildingRef: this.buildingRef,
        tenantRef: this.tenantRef,
        actType: this.actType,
        actDate: this.actDate,
        rateId: this.rateId,
        // durationHours intentionally omitted — server defaults to 1; not used in formula (PR2 §4)
        quantity: this.quantity,
        notes: this.notes,
        includeInReport: this.includeInReport
      },
      bubbles: true,
      composed: true,
    }));
  }
}
